import { PlusIcon } from 'lucide-react';
import { useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslate } from 'sbx-crm-menu-ui';
import { AppAgentContext } from 'src/context/ContextProvider';
import { appActions } from 'src/store';
import { ModalType } from 'src/types/Modal';
import { AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { grantPermission } from 'src/utils';
import { Badge } from '../ui/badge';
import RolesComponent from './RolesComponent';

const classNameLabel = 'form-input-label';

interface Props {
    values: string[];
    label: string;
    isRequired?: boolean;
    onChange: (value: string[]) => void;
    title?: string;
    description?: string;
    placeholder?: string;
}

export const OptionsAddComponent = ({ values, label, onChange, isRequired, title, description, placeholder }: Props) => {
    const {
        state: {
            session: { permissions },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const { id } = useParams();
    const { t: c } = useTranslate('common');
    const { t: agent } = useTranslate('agent');
    const [isHover, setIsHover] = useState(false);

    const allowEdit = useMemo(() => {
        return grantPermission(AI_AGENT_V2_EDIT, permissions);
    }, [permissions]);

    const openModal = () => {
        dispatch(
            appActions.openModal({
                type: ModalType.DYNAMIC,
                data: {
                    // title: c('add_options'),
                    state: values,
                    className: 'bg-gradient-to-br from-pink-100 to-indigo-100',
                    children: (val, setVal) => {
                        return (
                            <RolesComponent
                                value={val}
                                setValue={(items) => {
                                    onChange(items);
                                    setVal(items);
                                }}
                                title={title ?? agent('add_options')}
                                description={description ?? agent('options_description')}
                                label={label}
                                placeholder={placeholder ?? c('add_option')}
                            />
                        );
                    },
                    onClose: () => dispatch(appActions.closeModal(ModalType.DYNAMIC)),
                },
            })
        );
    };

    return (
        <div className="relative flex flex-col">
            <label className={`block text-sm leading-6 ${classNameLabel}`}>
                {label}
                {isRequired && <sup className="text-[#dc3545] ml-1">*</sup>}
            </label>
            <button disabled={id !== 'create' && !allowEdit} onClick={openModal}>
                <div
                    onMouseOver={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    className={`w-full flex items-center h-[41.5px] font-medium rounded border-purple-300 border pl-2 pr-10 py-2 text-sm bg-background outline-none ${id !== 'create' && !allowEdit ? 'cursor-not-allowed' : 'cursor-pointer focus:ring-2 focus:ring-primary focus:ring-inset hover:bg-accent hover:text-accent-foreground'}`}
                >
                    <div className="flex gap-2 items-center whitespace-nowrap overflow-hidden">
                        {values.map((val) => (
                            <Badge key={val} variant="default" className="flex items-center gap-1 py-[5px] shrink-0">
                                {val}
                            </Badge>
                        ))}
                        {values.length === 0 && <p className="text-gray-400">{agent('click_add_option')}</p>}
                    </div>
                </div>
                <div
                    className={`pointer-events-none absolute right-10 bottom-[1px] h-[40px] w-[30px]
           bg-gradient-to-l ${isHover ? 'from-accent' : 'from-white'} to-transparent`}
                />
                <PlusIcon
                    className={`absolute top-1/2 right-2 text-gray-400 w-[20px] transition-all ${id !== 'create' && !allowEdit ? 'cursor-not-allowed' : 'cursor-pointer hover:text-gray-500 hover:scale-110'}`}
                />
            </button>
        </div>
    );
};
